<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, toRefs, watch } from "vue";
import { until, useBreakpoints, useSwipe, useWindowSize } from "@vueuse/core";
import type { IMedia } from "@/interfaces";
import type { SlideDirections } from "@/types";
import { BREAKPOINTS } from "@/consts";
import Button from "@/stories/buttons/Button/Button.vue";
import converters from "@/converters";

interface Props {
  medias: IMedia[];
  defaultIndex?: number;
}

const props = withDefaults(defineProps<Props>(), {
  defaultIndex: 0,
});

const emit = defineEmits<{
  (e: "close"): void;
}>();

const { medias, defaultIndex } = toRefs(props);

const dialogRef = ref<HTMLDialogElement>();
const index = ref(defaultIndex.value);
const loading = ref(true);
const slideDirection = ref<SlideDirections>();
const contentRef = ref<HTMLDivElement>();
const contentHeight = ref(0);
const contentWidth = ref(0);

const { width, height } = useWindowSize();
const breakpoints = useBreakpoints(BREAKPOINTS);
const isDesktop = breakpoints.greaterOrEqual("desktop");

const { isSwiping, direction } = useSwipe(contentRef);

const openDialog = () => {
  if (!dialogRef.value) {
    throw new Error("Dialog reference is not valid");
  }

  dialogRef.value.showModal();
  dialogRef.value.addEventListener("cancel", handleCancelEvent);
};

const handleCancelEvent = (event: Event) => {
  emit("close");
  event.stopPropagation();
};

const nextImage = () => {
  loading.value = true;
  if (medias.value.length > index.value + 1) {
    index.value = index.value + 1;
    slideDirection.value = "animate-slide-left";
  }
};

const prevImage = () => {
  loading.value = true;
  if (index.value > 0) {
    index.value = index.value - 1;
    slideDirection.value = "animate-slide-right";
  }
};

const onImageLoaded = () => {
  loading.value = false;
};

const resetAnimation = () => {
  slideDirection.value = undefined;
};

const preloadImages = () => {
  medias.value
    .filter((item) => item.type === "image")
    .forEach((item) => {
      const img = new Image();
      img.src = item.url;
    });
};

const handleKey = (e: KeyboardEvent) => {
  e.stopPropagation();
  if (e.key === "ArrowRight") {
    nextImage();
  } else if (e.key === "ArrowLeft") {
    prevImage();
  }
};

const setContentSize = () => {
  contentWidth.value = contentRef.value?.getBoundingClientRect().width || 0;

  // We can use the height by using the width
  const ratioHeight = contentWidth.value * 0.6;

  // But there are some possible height limit. If the browser's height is too
  // small, we should use that
  const possibleHeight =
    contentRef.value?.getBoundingClientRect().height || ratioHeight;

  // So the minimum value should be used here
  contentHeight.value = Math.min(ratioHeight, possibleHeight);
};

watch(
  () => width.value,
  () => setContentSize(),
);

watch(
  () => height.value,
  () => setContentSize(),
);

watch(
  () => isSwiping.value,
  () => {
    if (!isSwiping.value) {
      return;
    }

    if (direction.value === "left") {
      nextImage();
    } else if (direction.value === "right") {
      prevImage();
    }
  },
);

onMounted(async () => {
  preloadImages();
  await until(dialogRef).toBeTruthy();
  openDialog();
  document.body.style.overflowY = "hidden";
  window.addEventListener("keydown", handleKey);
  setContentSize();
});

onUnmounted(() => {
  dialogRef?.value?.close();
  dialogRef?.value?.removeEventListener("cancel", handleCancelEvent);
  document.body.style.overflowY = "auto";
  window.removeEventListener("keydown", handleKey);
});

const activeMedia = computed(() => {
  return medias.value[index.value];
});

const activeLink = computed(() => {
  return activeMedia?.value?.url;
});

const isVideo = computed(() => {
  if (
    activeLink.value?.includes("youtube.com") ||
    activeLink.value?.includes("vimeo.com") ||
    activeLink.value?.includes("dreambroker.com")
  ) {
    return true;
  }

  return false;
});

const isImage = computed(() => !isVideo.value);

const videoLink = computed(() =>
  converters.medias.toVideoLink(activeLink.value || ""),
);
</script>

<template>
  <dialog
    ref="dialogRef"
    class="p-0 m-0 w-screen h-screen flex flex-col bg-transparent"
    style="max-width: none; max-height: none"
    :class="{
      'px-[3rem] py-[2rem]': isDesktop,
    }"
  >
    <div
      class="relative flex justify-center items-center h-[5.375rem] desktop:h-[2.875rem] z-50"
    >
      <Button
        type="grey"
        :text="isDesktop ? $t('global.close') : ''"
        icon="fas fa-times"
        class="absolute left-5 top-5 desktop:left-0 desktop:top-0"
        @click="$emit('close')"
      />
      <div v-if="medias.length > 1" class="text-neutral0 h500">
        {{ index + 1 }}/{{ medias.length }}
      </div>
    </div>
    <div
      class="w-full h-full overflow-hidden flex justify-center items-center relative pb-[5.375rem] desktop:pb-0 z-50"
      :class="{ 'p-[5rem]': isDesktop }"
      ref="contentRef"
    >
      <Button
        v-if="index > 0"
        type="grey"
        size="sm"
        icon="fas fa-chevron-left test-left-button"
        class="absolute left-2 desktop:left-0"
        @click="prevImage"
      />
      <img
        v-if="isImage && activeMedia"
        :src="activeLink"
        :alt="activeMedia.title"
        class="desktop:rounded max-w-full max-h-full object-contain"
        :class="[slideDirection]"
        @animationend="resetAnimation"
        @load="onImageLoaded"
      />
      <div :class="[slideDirection]" @animationend="resetAnimation">
        <iframe
          v-if="isVideo"
          :width="contentWidth - 80"
          :height="contentHeight - 80"
          :src="videoLink"
          title="YouTube video player"
          frameborder="0"
          allow="
            accelerometer;
            autoplay;
            clipboard-write;
            encrypted-media;
            gyroscope;
            picture-in-picture;
            web-share;
          "
          allowfullscreen
        ></iframe>
      </div>
      <Button
        v-if="index < medias.length - 1"
        type="grey"
        size="sm"
        icon="fas fa-chevron-right"
        class="absolute right-2 desktop:right-0 test-right-button"
        @click="nextImage"
      />
    </div>
    <div
      class="bg-neutral1000 opacity-95 fixed left-0 top-0 w-screen h-screen"
    />
  </dialog>
</template>
